import React from 'react'
import APIDocumentation from '@/components/common/APIDocumentation'
import HomeLayout from '@/components/layouts/HomeLayout'


const APIDocumentations: React.FC = () => {
    const apiKey = 'pk_your_api_key'
    const apiSecret = 'sk_your_api_secret'
    const baseUrl = 'https://api.pexiblock.com/api'

    const sections: any[] = [
        {
            title: 'Payment Endpoints',
            endpoints: [
                {
                    id: 'payment-create',
                    title: 'Create Payment',
                    endpoint: `${baseUrl}/payment/create/`,
                    method: 'POST',
                    description: 'Create a new payment transaction. This endpoint allows you to initiate a payment with optional customer details. The response includes a payment URL where the customer can complete the transaction.',
                    headers: {
                        'X-API-Key': apiKey,
                        'X-API-Secret': apiSecret,
                        'Content-Type': 'application/json',
                    },
                    requestBody: {
                        total_amount: '100.00',
                        currency_code: 'USD',
                        payment_method: 'card',
                        external_reference: 'TEST-129000-001',
                        customer_details: {
                            email: 'customer@example.com',
                            first_name: 'John',
                            last_name: 'Doe',
                            phone: '+1234567890',
                            date_of_birth: '1990-01-01',
                            country_of_residence: 'US',
                            state_of_residence: 'CA',
                            card_country_code: 'US',
                            card_state_code: 'CA',
                            card_city: 'San Francisco',
                            card_post_code: '94102',
                            card_street: '123 Market St',
                        },
                        metadata: {
                            order_id: '12345',
                            description: 'Product purchase',
                        },
                    },
                    sampleResponse: {
                        success: true,
                        message: 'Payment created successfully',
                        data: {
                            transaction_reference: 'TXN-1234567890',
                            payment_url: 'https://api.pexiblock.com/pay/payment-uuid',
                            total_amount: '100.00',
                            currency: 'USD',
                            status: 'pending',
                        },
                    },
                    errorResponses: [
                        {
                            code: 400,
                            message: 'Bad Request',
                            description: 'Invalid request parameters or missing required fields',
                        },
                        {
                            code: 401,
                            message: 'Unauthorized',
                            description: 'Invalid or missing API credentials',
                        },
                        {
                            code: 500,
                            message: 'Internal Server Error',
                            description: 'An error occurred while processing the payment',
                        },
                    ],
                    notes: [
                        'Customer details are optional but recommended for better conversion rates',
                        'External reference should be unique for each payment',
                        'Amount should be provided as a string with 2 decimal places',
                        'Supported currencies: USD, EUR, GBP, NGN, KES, ZAR',
                        'Payment method defaults to "card" but can support other methods',
                    ],
                },
                {
                    id: 'payment-status',
                    title: 'Get Transaction Status',
                    endpoint: `${baseUrl}/payment/{transaction_reference}/status/`,
                    method: 'GET',
                    description: 'Retrieve the current status of a transaction. Useful for polling the payment status after redirecting the customer to the payment page.',
                    headers: {
                        'X-API-Key': apiKey,
                        'X-API-Secret': apiSecret,
                        'Content-Type': 'application/json',
                    },
                    pathParams: {
                        transaction_reference: 'The unique transaction reference returned from payment creation (e.g., TXN-1234567890)',
                    },
                    sampleResponse: {
                        success: true,
                        data: {
                            id: 1,
                            transaction_reference: 'TXN-1234567890',
                            total_amount: '100.00',
                            currency: 'USD',
                            status: 'completed',
                            payment_method: 'card',
                            customer: {
                                id: 123,
                                email: 'customer@example.com',
                                first_name: 'John',
                                last_name: 'Doe',
                            },
                            created_at: '2024-01-15T10:30:00Z',
                            updated_at: '2024-01-15T10:35:00Z',
                        },
                    },
                    errorResponses: [
                        {
                            code: 404,
                            message: 'Not Found',
                            description: 'Transaction with the provided reference not found',
                        },
                        {
                            code: 401,
                            message: 'Unauthorized',
                            description: 'Invalid or missing API credentials',
                        },
                    ],
                    notes: [
                        'Possible transaction statuses: pending, completed, failed, cancelled',
                        'Use this endpoint to verify payment completion for webhooks or polling',
                        'Response includes full transaction details for record keeping',
                    ],
                },
            ],
        },
        {
            title: 'Webhook Endpoints',
            endpoints: [
                {
                    id: 'webhook-transaction',
                    title: 'Transaction Webhook',
                    endpoint: `${baseUrl}/webhooks/transaction/`,
                    method: 'POST',
                    description: 'Webhook endpoint for receiving transaction events. Configure your webhook URL in the dashboard to receive real-time payment notifications.',
                    requestBody: {
                        event: 'transaction.completed',
                        transaction_id: 'TXN-1234567890',
                        status: 'completed',
                        amount: '100.00',
                        currency: 'USD',
                        customer_email: 'customer@example.com',
                        timestamp: '2024-01-15T10:35:00Z',
                        metadata: {
                            order_id: '12345',
                        },
                    },
                    sampleResponse: {
                        success: true,
                        message: 'Webhook received successfully',
                    },
                    notes: [
                        'Webhooks are sent asynchronously after payment completion',
                        'All webhooks include a signature header for verification',
                        'Your endpoint should respond with 2xx status code within 30 seconds',
                        'Failed webhook deliveries are retried up to 5 times',
                        'Supported events: transaction.created, transaction.completed, transaction.failed',
                    ],
                },
                {
                    id: 'webhook-wert',
                    title: 'Wert Webhook',
                    endpoint: `${baseUrl}/webhooks/wert/`,
                    method: 'POST',
                    description: 'Webhook endpoint for Wert.io payment gateway events. Handles payment status updates from Wert integration.',
                    requestBody: {
                        event: 'payment.success',
                        transaction_reference: 'TXN-1234567890',
                        wert_transaction_id: 'WERT-789456123',
                        status: 'completed',
                    },
                    sampleResponse: {
                        success: true,
                        message: 'Wert webhook processed',
                    },
                    notes: [
                        'Triggered by Wert.io payment completion',
                        'Updates transaction status based on Wert response',
                        'Includes both success and failure scenarios',
                    ],
                },
            ],
        },
    ]

    return (
        <HomeLayout>
            <div className="min-h-screen bg-gradient-to-b from-gray-950 via-gray-900 to-gray-950 py-12 px-4 sm:px-6 lg:px-8">
                <div className="max-w-7xl mx-auto">
                    <APIDocumentation
                        isComprehensive={true}
                        sections={sections}
                        apiKey={apiKey}
                        apiSecret={apiSecret}
                        baseUrl={baseUrl}
                        className="w-full"
                    />
                </div>
            </div>
        </HomeLayout>
    )
}

export default APIDocumentations
